import { z } from 'zod'

export const ENTITY_PREFIXES = {
  load: 'LD',
  bid: 'BD',
  transport: 'TR',
  vehicle: 'VH',
  route: 'RT',
} as const

export type EntityPrefix = (typeof ENTITY_PREFIXES)[keyof typeof ENTITY_PREFIXES]

/** Format a numeric ID for display and URLs: LD-42 */
export function formatUrlId(prefix: EntityPrefix, id: number): string {
  return `${prefix}-${id}`
}

/** Parse "LD-42" or "42" -> number */
export function parseUrlId(value: string, expectedPrefix?: EntityPrefix): number {
  const match = value.match(/^([A-Z]{2})[#-](\d+)$/)
  if (match) {
    if (expectedPrefix && match[1] !== expectedPrefix)
      throw new Error(`Expected prefix ${expectedPrefix}, got ${match[1]}`)
    return Number(match[2])
  }
  const num = Number(value)
  if (Number.isNaN(num) || !Number.isInteger(num) || num <= 0)
    throw new Error(
      `Invalid ID format: "${value}". Expected ${expectedPrefix ? `${expectedPrefix}-N or ` : ''}positive integer`,
    )
  return num
}

/** Zod transform: string param -> number (accepts "LD-42" or "42") */
function zodHumanIdParam(prefix: EntityPrefix) {
  return z.string().transform((val, ctx) => {
    try {
      return parseUrlId(val, prefix)
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Invalid ${prefix} ID` })
      return z.NEVER
    }
  })
}

export const ZodLoadIdParam = zodHumanIdParam('LD')
export const ZodBidIdParam = zodHumanIdParam('BD')
export const ZodTransportIdParam = zodHumanIdParam('TR')
export const ZodVehicleIdParam = zodHumanIdParam('VH')
export const ZodRouteIdParam = zodHumanIdParam('RT')

/** Route param objects: z.object({ id: ZodXxxIdParam }) for use in Elysia route params */
export const ZodLoadIdParams = z.object({ id: ZodLoadIdParam })
export type LoadIdParams = z.output<typeof ZodLoadIdParams>

export const ZodVehicleIdParams = z.object({ id: ZodVehicleIdParam })
export type VehicleIdParams = z.output<typeof ZodVehicleIdParams>

export const ZodRouteIdParams = z.object({ id: ZodRouteIdParam })
export type RouteIdParams = z.output<typeof ZodRouteIdParams>

export const ZodBidIdParams = z.object({ id: ZodBidIdParam })
export type BidIdParams = z.output<typeof ZodBidIdParams>

export const ZodTransportIdParams = z.object({ id: ZodTransportIdParam })
export type TransportIdParams = z.output<typeof ZodTransportIdParams>
